import { useState } from "react";
import type { Actuacion, Expediente, MovimientoEconomico } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input } from "../../components/Formulario";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

function fechaMasReciente(fechas: string[]): string | null {
  if (fechas.length === 0) return null;
  return fechas.reduce((max, f) => (f > max ? f : max));
}

function diasEntre(desde: string, hasta: string): number {
  const ms = new Date(hasta).getTime() - new Date(desde).getTime();
  return Math.max(Math.round(ms / 86400000), 0);
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <p className="mt-1 text-sm text-texto">{valor}</p>
    </div>
  );
}

// Cronómetro del expediente + Avance interno (barra de progreso manual).
// "Última actuación" y "Último movimiento" se derivan de datos ya
// disponibles en la ficha (actuaciones/movimientos económicos) — no
// requieren una llamada nueva al backend. "Inicio del juicio" no es
// derivable (no hay señal confiable) y es el único campo manual además
// del avance.
export function PanelCronometro({
  expediente,
  actuaciones,
  movimientos,
  onCambio,
}: {
  expediente: Expediente;
  actuaciones: Actuacion[];
  movimientos: MovimientoEconomico[];
  onCambio: () => void;
}) {
  const { mostrar } = useToast();
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(expediente.avance_manual_pct);
  const [fechaInicioJuicio, setFechaInicioJuicio] = useState(expediente.fecha_inicio_juicio ?? "");

  const ultimaActuacion = fechaMasReciente(actuaciones.map((a) => a.fecha));
  const ultimoMovimiento = fechaMasReciente(movimientos.map((m) => m.fecha));
  const tiempoTotalDias = diasEntre(expediente.fecha_apertura, expediente.fecha_cierre ?? new Date().toISOString().substring(0, 10));

  async function guardar() {
    setGuardando(true);
    try {
      await api.put(`/expedientes/${expediente.id}/progreso`, {
        avance_manual_pct: avance,
        fecha_inicio_juicio: fechaInicioJuicio || null,
      });
      mostrar("Avance actualizado.", "exito");
      setEditando(false);
      onCambio();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el avance.", "error");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Tarjeta>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-texto">Cronómetro del expediente</h2>
        {!editando && (
          <Boton tamano="sm" variante="secundario" onClick={() => setEditando(true)}>
            Editar avance
          </Boton>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Dato etiqueta="Ingreso" valor={expediente.fecha_apertura} />
        <Dato etiqueta="Inicio del juicio" valor={expediente.fecha_inicio_juicio ?? "Sin definir"} />
        <Dato etiqueta="Última actuación" valor={ultimaActuacion ?? "—"} />
        <Dato etiqueta="Último movimiento" valor={ultimoMovimiento ?? "—"} />
        <Dato etiqueta="Tiempo total" valor={`${tiempoTotalDias} días`} />
      </div>

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="font-medium text-texto">Avance interno</span>
          <span className="text-texto-secundario">{expediente.avance_manual_pct}% — no representa % legal</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-borde">
          <div
            className="h-full rounded-full bg-secundario"
            style={{ width: `${Math.max(expediente.avance_manual_pct, 3)}%` }}
          />
        </div>
      </div>

      {editando && (
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-borde pt-4 sm:grid-cols-2">
          <Campo etiqueta="Avance interno (0-100)">
            <Input
              type="number"
              min={0}
              max={100}
              value={avance}
              onChange={(e) => setAvance(Number(e.target.value))}
            />
          </Campo>
          <Campo etiqueta="Fecha de inicio del juicio">
            <Input type="date" value={fechaInicioJuicio} onChange={(e) => setFechaInicioJuicio(e.target.value)} />
          </Campo>
          {/* Mismo orden Cancelar → Guardar que el resto de los formularios
              del módulo (FormularioExpediente, PestanaAgenda,
              PestanaActuaciones, PestanaPartes, PestanaEconomico) — antes
              este era el único bloque con el orden invertido. */}
          <div className="flex items-end gap-2 sm:col-span-2">
            <Boton variante="secundario" onClick={() => setEditando(false)} disabled={guardando}>
              Cancelar
            </Boton>
            <Boton onClick={guardar} cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </div>
      )}
    </Tarjeta>
  );
}
